import React, { useState, useEffect } from 'react';
import { X, FileText, Clock, CheckCircle, XCircle, RefreshCw, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { api } from '../services/api';

interface TemplatesListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Template {
  name: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  category?: string;
  language?: string;
}

interface TemplateDetails {
  name: string;
  status: string;
  category?: string;
  language?: string;
  components?: Array<{
    type: string;
    format?: string;
    text?: string;
    buttons?: Array<{
      type: string;
      text: string;
      phone_number?: string;
      url?: string;
    }>;
  }>;
}

export const TemplatesListModal: React.FC<TemplatesListModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [templateDetails, setTemplateDetails] = useState<Record<string, TemplateDetails>>({});
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSyncMessage(null);
      const data = await api.getTemplates();
      setTemplates(data.data || []);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Falha ao carregar templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncStatuses = async () => {
    try {
      setIsSyncing(true);
      setError(null);
      const result = await api.syncTemplateStatuses();
      setSyncMessage(`${result.updated} template(s) sincronizado(s) com sucesso!`);
      // Reload templates to show updated statuses
      await loadTemplates();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Falha ao sincronizar templates');
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          label: 'Aprovado',
        };
      case 'PENDING':
        return {
          icon: <Clock className="w-4 h-4" />,
          className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          label: 'Pendente',
        };
      case 'REJECTED':
        return {
          icon: <XCircle className="w-4 h-4" />,
          className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
          label: 'Rejeitado',
        };
      default:
        return {
          icon: <Clock className="w-4 h-4" />,
          className: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          label: status,
        };
    }
  };

  const getCategoryLabel = (category?: string) => {
    switch (category) {
      case 'UTILITY':
        return 'Utilidade';
      case 'MARKETING':
        return 'Marketing';
      case 'AUTHENTICATION':
        return 'Autenticação';
      default:
        return category || '-';
    }
  };

  const handleTemplateClick = async (templateName: string) => {
    if (expandedTemplate === templateName) {
      // Collapse if already expanded
      setExpandedTemplate(null);
      return;
    }

    // Expand template
    setExpandedTemplate(templateName);

    // Load details if not already loaded
    if (!templateDetails[templateName]) {
      try {
        setLoadingDetails({ ...loadingDetails, [templateName]: true });
        const details = await api.getTemplateDetails(templateName);
        setTemplateDetails({ ...templateDetails, [templateName]: details });
      } catch (error) {
        console.log('Template details not available');
        // Set a placeholder if details are not available
        setTemplateDetails({
          ...templateDetails,
          [templateName]: { name: templateName, status: 'UNKNOWN' },
        });
      } finally {
        setLoadingDetails({ ...loadingDetails, [templateName]: false });
      }
    }
  };

  const renderTemplateDetails = (templateName: string) => {
    const details = templateDetails[templateName];
    const isLoadingDetail = loadingDetails[templateName];

    if (isLoadingDetail) {
      return (
        <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">Carregando detalhes...</p>
        </div>
      );
    }

    if (!details || !details.components) {
      return (
        <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Detalhes do template não disponíveis. Este template está registrado mas seus detalhes não podem ser visualizados via API.
          </p>
        </div>
      );
    }

    const headerComponent = details.components.find((c) => c.type === 'HEADER');
    const bodyComponent = details.components.find((c) => c.type === 'BODY');
    const footerComponent = details.components.find((c) => c.type === 'FOOTER');
    const buttonsComponent = details.components.find((c) => c.type === 'BUTTONS');

    return (
      <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
        <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase">
          Conteúdo do Template
        </p>

        {headerComponent && (
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cabeçalho:</p>
            <p className="text-sm text-gray-900 dark:text-white font-medium bg-white dark:bg-gray-800 p-2 rounded">
              {headerComponent.text}
            </p>
          </div>
        )}

        {bodyComponent && (
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Corpo:</p>
            <p className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded whitespace-pre-wrap">
              {bodyComponent.text}
            </p>
          </div>
        )}

        {footerComponent && (
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Rodapé:</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded">
              {footerComponent.text}
            </p>
          </div>
        )}

        {buttonsComponent && buttonsComponent.buttons && buttonsComponent.buttons.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Botões:</p>
            <div className="space-y-2">
              {buttonsComponent.buttons.map((button, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700"
                >
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {button.text}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Tipo: {button.type === 'PHONE_NUMBER' ? 'Telefone' : button.type === 'URL' ? 'Link' : 'Resposta Rápida'}
                    {button.phone_number && ` - ${button.phone_number}`}
                    {button.url && ` - ${button.url}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-3xl z-50">
          <div className="bg-white dark:bg-gray-800 px-6 pt-5 pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Meus Templates
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleSyncStatuses}
                  disabled={isSyncing || isLoading}
                  className="px-3 py-2 text-sm bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg transition disabled:opacity-50 flex items-center space-x-2"
                  title="Sincronizar status com Meta"
                >
                  <Download className={`w-4 h-4 ${isSyncing ? 'animate-bounce' : ''}`} />
                  <span>{isSyncing ? 'Sincronizando...' : 'Sincronizar'}</span>
                </button>
                <button
                  onClick={loadTemplates}
                  disabled={isLoading}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition disabled:opacity-50"
                  title="Atualizar"
                >
                  <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
                {error}
              </div>
            )}

            {syncMessage && (
              <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
                {syncMessage}
              </div>
            )}

            <div className="max-h-[60vh] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhum template encontrado
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    Crie um novo template para começar
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((template, index) => {
                    const statusBadge = getStatusBadge(template.status);
                    const isExpanded = expandedTemplate === template.name;
                    return (
                      <div
                        key={`${template.name}-${index}`}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                      >
                        <div
                          onClick={() => handleTemplateClick(template.name)}
                          className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                  {template.name}
                                </h4>
                                <span
                                  className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}
                                >
                                  {statusBadge.icon}
                                  <span>{statusBadge.label}</span>
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                                {template.category && (
                                  <span>
                                    <strong>Categoria:</strong> {getCategoryLabel(template.category)}
                                  </span>
                                )}
                                {template.language && (
                                  <span>
                                    <strong>Idioma:</strong> {template.language.replace('_', '-')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="ml-4">
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="px-4 pb-4">
                            {renderTemplateDetails(template.name)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-3 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
